/**
 * KeyResultListCard.tsx
 *
 * Renders the `key_result_list` envelope data emitted by onething_list_key_results.
 * Mirrors ObjectiveListCard with an added weight chip. Exports `progressFillClass`
 * (status-driven progress bar color) reused by ObjectiveListCard/ObjectiveDetailCard.
 */

import { ListChecks, Inbox } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { OkrStatusBadge } from "@/components/okr/OkrStatusBadge";
import { ObjectiveStatus, KeyResultStatus } from "@/types";

// ---------------------------------------------------------------------------
// Data shape for kind "key_result_list"
// ---------------------------------------------------------------------------

export interface KeyResultListRow {
  code: string;
  name: string;
  status: string;
  progress: number;
  targetProgress: number;
  weight?: number;
}

// ---------------------------------------------------------------------------
// Status-driven progress fill — shared with Objective cards
// ---------------------------------------------------------------------------

const PROGRESS_FILL: Record<ObjectiveStatus, string> = {
  [ObjectiveStatus.DRAFT]: "bg-muted-foreground/40",
  [ObjectiveStatus.ACTIVE]: "bg-gradient-to-r from-blue-500 to-blue-400",
  [ObjectiveStatus.COMPLETED]: "bg-gradient-to-r from-emerald-500 to-emerald-400",
  [ObjectiveStatus.CANCELLED]: "bg-gradient-to-r from-destructive to-destructive/70",
  [ObjectiveStatus.ON_HOLD]: "bg-gradient-to-r from-amber-500 to-amber-400",
};

export function progressFillClass(status?: string): string {
  return PROGRESS_FILL[status as ObjectiveStatus] ?? PROGRESS_FILL[ObjectiveStatus.DRAFT];
}

// ---------------------------------------------------------------------------
// Exported KR row — reused by ObjectiveDetailCard
// ---------------------------------------------------------------------------

export function KeyResultRow({ row }: { row: KeyResultListRow }) {
  const pct =
    row.targetProgress > 0 ? Math.round((row.progress / row.targetProgress) * 100) : 0;

  return (
    <div className="mx-1 rounded-lg px-2 py-2.5 transition-colors hover:bg-muted/50 cursor-default">
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="font-mono text-[10.5px] font-semibold text-muted-foreground">
          {row.code}
        </span>
        <span className="text-[13px] font-semibold tracking-tight flex-1 min-w-0 truncate">
          {row.name}
        </span>
        <OkrStatusBadge status={row.status as KeyResultStatus} size="sm" className="rounded-full" />
        {row.weight != null && (
          <Badge variant="outline" className="rounded-full text-[10.5px] px-2 py-0">
            {row.weight}%
          </Badge>
        )}
      </div>
      <div className="flex items-center gap-2 mt-1.5">
        <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden shadow-inner">
          <div
            className={`h-full rounded-full ${progressFillClass(row.status)}`}
            style={{ width: `${Math.min(pct, 100)}%` }}
          />
        </div>
        <span className="text-[10.5px] font-semibold text-muted-foreground tabular-nums whitespace-nowrap">
          {row.progress}/{row.targetProgress} · {pct}%
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

interface KeyResultListCardProps {
  summary: string;
  rows: KeyResultListRow[];
}

export function KeyResultListCard({ summary, rows }: KeyResultListCardProps) {
  return (
    <Card className="my-2 rounded-xl border-border/60 shadow-sm dark:shadow-none dark:ring-1 dark:ring-border/40 overflow-hidden">
      <CardHeader className="flex flex-row items-start gap-2.5 px-4 py-3">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <ListChecks className="h-3.5 w-3.5" />
        </div>
        <p className="text-[13.5px] font-bold leading-tight tracking-tight flex-1 min-w-0">
          {summary}
        </p>
        <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10.5px] font-semibold text-muted-foreground">
          {rows.length}
        </span>
      </CardHeader>
      <CardContent className="px-2 pb-2 pt-0">
        {rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-8 text-center text-muted-foreground">
            <Inbox className="h-6 w-6 opacity-50" />
            <span className="text-xs">No key results found.</span>
          </div>
        ) : (
          rows.map((row) => <KeyResultRow key={row.code} row={row} />)
        )}
      </CardContent>
    </Card>
  );
}
